import axios from 'axios';
import qs from 'qs';

// Parent
import applicationSummary from '@/parent/services/api/application-summary';
import { asyncApi, handleError } from '@/shared/services/api';
import claim from '@/parent/services/api/claim';
// Shared
import cohortCourseCompletion from '@/shared/services/api/cohort-course-completion';
import cohortLearners from '@/shared/services/api/cohort-learners';
import cohortLesson from '@/shared/services/api/cohort-lesson';
import cohortMember from '@/shared/services/api/cohort-member';
import comment from '@/shared/services/api/comment';
import eligibilityScreener from '@/parent/services/api/eligibility-screener';
import enrollment from '@/parent/services/api/enrollment';
import familySubsidy from '@/parent/services/api/family-subsidy';
import form from '@/parent/services/api/form';
import householdMember from '@/parent/services/api/household-member';
import member from '@/shared/services/api/member';
import { normalizedPortalBaseUrlPathPrefix } from '@/shared/utils/portal-base-url-path';
import pageSettings from '@/parent/services/api/page-settings';
import provider from '@/shared/services/api/provider';
import publicApi from '@/shared/services/api/public-api';
import screenerProgram from '@/shared/services/api/public/screener-program';
import sharedQuestionSet from '@/shared/services/api/shared/question-set';
import translations from '@/shared/services/api/translations';

export default {
  assignmentCompletion: {
    ...asyncApi({
      endpoint: '/assignment_completions',
      payloadKey: 'assignment_completion',
    }),

    async scormLaunch(id, successCallback = null, failureCallback = null) {
      return axios
        .post(`${window.api_url}/assignment_completions/${id}/scorm_launch`)
        .then((response) => {
          if (successCallback) successCallback(response);
          return response;
        })
        .catch((error) => {
          if (failureCallback) return failureCallback(error);
          handleError(error);
          throw error;
        });
    },

    async scormSync(id, successCallback = null, failureCallback = null) {
      return axios
        .post(`${window.api_url}/assignment_completions/${id}/scorm_sync`)
        .then((response) => {
          if (successCallback) successCallback(response);
          return response;
        })
        .catch((error) => {
          if (failureCallback) return failureCallback(error);
          handleError(error);
          throw error;
        });
    },
  },

  cohort: {
    ...asyncApi({ endpoint: '/shared/cohorts' }),

    statistics: {
      show(cohortId) {
        return axios.get(`${window.api_url}/shared/cohorts/${cohortId}/statistics`);
      },

      index(cohortId, params) {
        return axios.get(`${window.api_url}/shared/cohorts/${cohortId}/statistics`, { params });
      },
    },
  },

  cohortCourseCompletion,

  cohortLearners,

  cohortLesson,

  cohortMember,

  comment,

  contact: {
    baseUrl(email, token = null) {
      const url = `${window.api_url}/public/organizations/${window.organization_id}/contacts/${encodeURIComponent(email)}`;
      if (!token) return url;

      return `${url}?${new URLSearchParams({ token }).toString()}`;
    },

    get(email, success, token = null) {
      if (!success) return axios.get(this.baseUrl(email, token));
      return axios.get(this.baseUrl(email, token)).then(success);
    },

    update(email, contact, success, token = null) {
      const request = axios.patch(this.baseUrl(email, token), { contact });
      if (!success) return request;
      return request.then(success);
    },
  },

  enrollment,

  eligibilityScreener,

  favorite: {
    baseUrl() {
      return `${window.api_url}/favorites`;
    },

    create(favorite, success) {
      axios.post(this.baseUrl(), { favorite }).then(success);
    },

    destroy(id, success) {
      axios.delete(`${this.baseUrl()}/${id}`).then(success);
    },

    index(success) {
      axios.get(this.baseUrl()).then(success);
    },
  },

  grant: {
    attachment: {
      baseUrl(grantId, id) {
        let url = `${window.api_url}/grants/${grantId}/attachments`;
        if (id) url += `/${id}`;
        return url;
      },

      create(grantId, attachment, success, failure) {
        axios.post(this.baseUrl(grantId), { attachment }).then(success).catch(failure);
      },

      destroy(grantId, id, success) {
        axios.delete(this.baseUrl(grantId, id)).then(success);
      },

      get(grantId, id, success) {
        axios.get(this.baseUrl(grantId, id)).then(success);
      },

      index(grantId, success) {
        axios.get(this.baseUrl(grantId)).then(success);
      },

      update(grantId, id, attachment, success, failure) {
        axios.patch(this.baseUrl(grantId, id), { attachment }).then(success).catch(failure);
      },
    },
  },

  downloadListsUrl(params) {
    const url = `${window.api_url}/download`;
    axios.get(url, { params, responseType: 'blob' }).then((resp) => {
      const windowUrl = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = windowUrl;
      link.setAttribute('download', `favorites.${params.format}`); // or any other extension
      document.body.appendChild(link);
      link.click();
    });
  },

  lessonCompletion: {
    baseUrl(id) {
      let url = `${window.api_url}/lesson_completions`;
      if (id) url += `/${id}`;
      return url;
    },

    get(id, success) {
      if (success) {
        return axios.get(this.baseUrl(id)).then(success);
      }

      return axios.get(this.baseUrl(id));
    },

    index(filters, success) {
      if (success) {
        return axios.get(this.baseUrl(), { params: filters }).then(success);
      }

      return axios.get(this.baseUrl(), { params: filters });
    },
  },

  list: {
    baseUrl(listId) {
      if (listId) {
        return `${window.api_url}/lists/${listId}`;
      }
      return `${window.api_url}/lists`;
    },

    create(list, success) {
      axios.post(this.baseUrl(), { list }).then(success);
    },

    destroy(listId, success) {
      axios.delete(this.baseUrl(listId)).then(success);
    },

    get(listId, success) {
      axios.get(this.baseUrl(listId)).then(success);
    },

    index(success) {
      axios.get(this.baseUrl()).then(success);
    },

    promiseIndex() {
      return axios.get(this.baseUrl());
    },

    update(listId, list, success) {
      axios.patch(this.baseUrl(listId), { list }).then(success);
    },
  },

  message: {
    baseUrl() {
      return `${window.api_url}/messages`;
    },

    create(message, success, failure) {
      axios.post(this.baseUrl(), { message }).then(success).catch(failure);
    },

    get(id, success) {
      axios.get(`${this.baseUrl()}/${id}`).then(success);
    },

    index(filters, success) {
      axios.get(this.baseUrl(), { params: filters }).then(success);
    },
    promiseCreate(message) {
      return axios.post(this.baseUrl(), { message });
    },
  },

  match: {
    baseUrl(searchId) {
      return `${window.api_url}/searches/${searchId}/matches`;
    },

    index(searchId, params, success) {
      if (!success) return axios.get(this.baseUrl(searchId), { params }).catch(handleError);
      axios.get(this.baseUrl(searchId), { params }).then(success);
    },
  },

  member,

  parent: {
    agreement: asyncApi({
      endpoint: '/parent/agreements',
      payloadKey: 'agreement',
    }),

    applicationSummary,

    attendance_event: {
      baseUrl(id) {
        let url = `${window.api_url}/parent/attendance_events`;
        if (id) url += `/${id}`;
        return url;
      },
      create(attendance_event, success, failure) {
        axios.post(this.baseUrl(), { attendance_event }).then(success).catch(failure);
      },
      get(id, success) {
        axios.get(this.baseUrl(id)).then(success);
      },
      index(filters, success) {
        axios.get(this.baseUrl(), { params: filters }).then(success);
      },

      update(id, attendance_event, success) {
        axios.patch(this.baseUrl(id), { attendance_event }).then(success);
      },
    },

    children: {
      baseUrl(groupId, childId = null) {
        let url = `${window.api_url}/parent/groups/${groupId}/children`;
        if (childId) url += `/${childId}`;
        return url;
      },

      async get(groupId, childId, params = {}) {
        return axios.get(this.baseUrl(groupId, childId), { params }).catch(handleError);
      },

      async create(groupId, child) {
        return axios.post(this.baseUrl(groupId), { child }).catch(handleError);
      },

      async index(groupId, params = {}) {
        return axios.get(this.baseUrl(groupId), { params }).catch(handleError);
      },

      async update(groupId, childId, child, params = {}) {
        return axios
          .patch(this.baseUrl(groupId, childId), { child }, { params })
          .catch(handleError);
      },

      async destroy(groupId, childId) {
        return axios.delete(this.baseUrl(groupId, childId)).catch(handleError);
      },
    },

    claim,

    eligibilityScreener,

    enrollment,

    enrollment_status: {
      baseUrl(id) {
        return `${window.api_url}/parent/enrollment_status/${id}`;
      },

      update(id, enrollment, success, failure) {
        return axios.patch(this.baseUrl(id), { enrollment }).then(success).catch(failure);
      },
    },

    family_subsidy: familySubsidy,

    form,

    group: asyncApi({ endpoint: '/parent/groups', payloadKey: 'group' }),

    householdMember,

    pageSettings,

    subsidy: {
      baseUrl(groupId, subsidyId = null) {
        let url = `${window.api_url}/parent/groups/${groupId}/subsidies`;
        if (subsidyId) url += `/${subsidyId}`;
        return url;
      },

      async get(groupId, subsidyId, params = {}) {
        return axios.get(this.baseUrl(groupId, subsidyId), { params }).catch(handleError);
      },

      async create(groupId, subsidy) {
        return axios.post(this.baseUrl(groupId), { subsidy }).catch(handleError);
      },

      async index(groupId, params = {}) {
        return axios.get(this.baseUrl(groupId), { params }).catch(handleError);
      },

      async update(groupId, subsidyId, subsidy, params = {}) {
        return axios
          .patch(this.baseUrl(groupId, subsidyId), { subsidy }, { params })
          .catch(handleError);
      },

      async destroy(groupId, subsidyId) {
        return axios.delete(this.baseUrl(groupId, subsidyId)).catch(handleError);
      },

      downloadUrl(groupId, subsidyId, locale, filename) {
        return `${this.baseUrl(groupId, subsidyId)}.pdf?filename=${encodeURIComponent(filename)}.pdf&locale=${locale}`;
      },
    },

    survey: {
      baseUrl() {
        return `${window.api_url}/parent/surveys`;
      },
      create(survey) {
        return axios.post(this.baseUrl(), { survey });
      },
    },
  },

  screenerProgram,

  program: {
    baseUrl(provider_id) {
      return `${window.api_url}/providers/${provider_id}/programs`;
    },

    create(provider_id, program, success, failure) {
      axios.post(this.baseUrl(provider_id), { program }).then(success).catch(failure);
    },

    destroy(provider_id, program_id, success, failure) {
      axios
        .delete(`${this.baseUrl(provider_id)}/${program_id}`)
        .then(success)
        .catch(failure || handleError);
    },

    index(provider_id, filters = {}) {
      return axios.get(this.baseUrl(provider_id), { params: filters }).catch(handleError);
    },

    update(provider_id, program_id, program, success, failure) {
      const url = `${this.baseUrl(provider_id)}/${program_id}`;

      if (!success) return axios.patch(url, { program }).catch(failure || handleError);

      axios.patch(url, { program }).then(success).catch(failure);
    },

    updateOrCreate(provider_id, program, success, failure) {
      if (program.id) {
        this.update(provider_id, program.id, program, success, failure);
      } else {
        this.create(provider_id, program, success, failure);
      }
    },
  },

  provider,

  publicApi,

  reservation: {
    baseUrl() {
      return `${window.api_url}/reservations`;
    },

    create(reservation, success, failure) {
      axios.post(this.baseUrl(), { reservation }).then(success).catch(failure);
    },
  },

  review: {
    baseUrl() {
      return `${window.api_url}/reviews`;
    },

    create(review, success) {
      axios.post(this.baseUrl(), { review }).then(success);
    },
  },

  shared: {
    attendanceLog: {
      ...asyncApi({
        endpoint: '/shared/attendance_logs',
        payloadKey: 'attendance_log',
      }),
      downloadUrl(params) {
        return `${this.baseUrl()}.csv?${qs.stringify({ ...params, filename: `${params.filename}.csv` })}`;
      },
      async bulkUpdate(ids, updateObject) {
        return axios
          .patch(`${this.baseUrl()}/bulk_update`, { ids, attendance_log: updateObject })
          .catch(handleError);
      },
    },
    awardTemplate: asyncApi({
      endpoint: '/shared/award_templates',
      payloadKey: 'award_template',
    }),
    awardCopayConfiguration: {
      ...asyncApi({
        endpoint: '/shared/award_copay_configurations',
        payloadKey: 'award_copay_configuration',
      }),
      reorder(id, params) {
        return axios.post(`${this.baseUrl(id)}/reorder`, params).catch(handleError);
      },
    },
    assessmentPublicRecord: {
      baseUrl(assessmentId) {
        return `${window.api_url}/shared/assessments/${assessmentId}/public_records`;
      },
      create(assessmentId, publicRecord) {
        return axios
          .post(this.baseUrl(assessmentId), { public_record: publicRecord })
          .catch(handleError);
      },
      index(assessmentId, params = {}) {
        return axios.get(this.baseUrl(assessmentId), { params }).catch(handleError);
      },
    },
    invoiceLineItemConfiguration: {
      ...asyncApi({
        endpoint: '/shared/invoice_line_item_configurations',
        payloadKey: 'invoice_line_item_configuration',
      }),
      duplicate(params) {
        return axios.post(`${this.baseUrl()}/duplicate`, params).catch(handleError);
      },
      reorder(id, params) {
        return axios.post(`${this.baseUrl(id)}/reorder`, params).catch(handleError);
      },
    },
    invoiceLineItemOption: asyncApi({
      endpoint: '/shared/invoice_line_item_options',
      payloadKey: 'invoice_line_item_option',
    }),
    operatingLog: {
      ...asyncApi({
        endpoint: '/shared/operating_logs',
        payloadKey: 'operating_log',
      }),
      downloadUrl(id, filename) {
        return `${this.baseUrl(id)}.pdf?filename=${encodeURIComponent(filename)}.pdf`;
      },
    },
    publicRecord: asyncApi({
      endpoint: '/shared/public_records',
      payloadKey: 'public_record',
    }),
    fundingSource: asyncApi({
      endpoint: '/shared/funding_sources',
      payloadKey: 'funding_source',
    }),
    questionSet: sharedQuestionSet,
    rateGroup: asyncApi({
      endpoint: '/shared/rate_groups',
      payloadKey: 'rate_group',
    }),
    rule: asyncApi({ endpoint: '/shared/rules', payloadKey: 'rule' }),
    ruleSet: asyncApi({
      endpoint: '/shared/rule_sets',
      payloadKey: 'rule_set',
    }),
    ruleSetResult: asyncApi({
      endpoint: '/shared/rule_set_results',
      payloadKey: 'rule_set_result',
    }),
    subsidyProgram: asyncApi({
      endpoint: '/shared/subsidy_programs',
      payloadKey: 'subsidy_program',
    }),
  },

  search: {
    baseUrl(searchId) {
      if (searchId) {
        return `${window.api_url}/searches/${searchId}`;
      }
      return `${window.api_url}/searches`;
    },

    create(search, success, error) {
      if (!success) return axios.post(this.baseUrl(), { search }).catch(handleError);
      axios.post(this.baseUrl(), { search }).then(success).catch(error);
    },

    get(id, success, failure) {
      if (!success) return axios.get(this.baseUrl(id)).catch(handleError);
      axios.get(this.baseUrl(id)).then(success).catch(failure);
    },

    index(success, failure) {
      if (!success) return axios.get(this.baseUrl()).catch(handleError);
      axios.get(this.baseUrl()).then(success).catch(failure);
    },

    update(id, search, success, failure) {
      if (!success) return axios.patch(this.baseUrl(id), { search }).catch(handleError);
      axios.patch(this.baseUrl(id), { search }).then(success).catch(failure);
    },
  },

  tour_reservation: {
    baseUrl(id) {
      let url = `${window.api_url}/tour_reservations`;
      if (id) url += `/${id}`;
      return url;
    },

    create(tour_reservation, success, failure) {
      axios.post(this.baseUrl(), { tour_reservation }).then(success).catch(failure);
    },

    index(filters, success) {
      axios.get(this.baseUrl(), { params: filters }).then(success);
    },
  },

  translations,

  version: {
    get() {
      return axios.get(`${normalizedPortalBaseUrlPathPrefix()}/version`).catch(handleError);
    },
  },
};
